import React, { useEffect, useState } from "react";
import SelectField from "../../../../../../global components/Forms/SelectField/SelectField";
import s from "./Table.module.scss";
import SearchInput from "../../../../../../global components/Forms/SearchInput/SearchInput";
import ReusableTable from "../../../../../../global components/Table/Table";
import NoData from "../../../../../../global components/Table/NoData/NoData";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
  swrMutationFetcher,
  SwrMutationFetcherArgType,
} from "../../../../../../libs/api/Endpoints";
import dayjs from "dayjs";
import clsx from "clsx";
import Paginate from "../../../../../../global components/Paginate/Pagination";
import ContactsGroup from "../../../../../../global components/Icons/ContactGroup/ContactsGroup";
import { UsersApiResponse } from "../../../../../../libs/Utils/Types/User";
import Unauthorised from "../../../../../../global components/Unauthorised";
import { ColumnType } from "antd/es/table";
import useSWRMutation from "swr/mutation";
import { generateResult } from "../../../../../../global components/ResultModal/ResultModal";
import { Button } from "antd";

interface DataType {
  key: string;

  email: string;

  status: boolean;
  createdAt: string;
}
type Prop = {
  filters: "" | "verified" | "unverified";
};
function Table({ filters }: Prop) {
  const token = UseAuthToken();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(50);
  const [filter, setFilter] = useState<string>("");

  const [unauthorised, setUnauthorised] = useState<
    boolean | { title: string; message: string }
  >(false);
  const {
    data: TableUsers,
    isLoading,
    error,
  } = useSWR<UsersApiResponse, any, any>(
    token
      ? [
          "api/admin/newsletter/subscribers",
          token,
          {
            page: currentPage,
            limit: pageItems,
            search: searchValue,
            ...(filter && { status: filter }),
          },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true, // Refetch when the window regains focus
      // Refetch every 10 seconds (10000 ms)
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );
  const { trigger: Download, isMutating: loadings } = useSWRMutation(
    `api/admin/newsletter/export${filter && `?status=${filter}`}`,
    swrMutationFetcher,
  );
  const downloadCsv = async () => {
    try {
      const fetcherArg: SwrMutationFetcherArgType = {
        token,

        requestOption: {
          method: "GET",
        },
      };
      const result: Blob = await Download(fetcherArg);
      console.log(result);
      // Create a URL for the downloaded blob
      const url = window.URL.createObjectURL(result);

      // Create a temporary <a> element to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "transactions.csv"); // Set the desired file name
      document.body.appendChild(link);
      link.click(); // Trigger the download
      document.body.removeChild(link); // Cleanup
    } catch (error) {
      const e = parseClientError(error);
      console.log(e.message);

      generateResult("error", e.message || "Something went wrong");
    }
  };
  const columns: ColumnType<DataType>[] = [
    {
      title: "Email address",
      dataIndex: "email",
      key: "email",
      render: (email: string) => <span className={clsx(s.cell)}>{email}</span>,
    },

    {
      title: "Subscription date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => (
        <span className={clsx(s.cell)}>
          {dayjs.utc(createdAt).local().format("MMM D, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: boolean) => (
        <span
          className={clsx(
            s.status,
            s.statusCell,
            status ? s.successful : s.failed,
          )}
        >
          {status ? "Verified" : "Unverified"}
        </span>
      ),
    },
  ];
  const transformedData = TableUsers?.data.map((user) => ({
    key: user.id,
    email: user.email || "N/A",

    status: user.isVerified || false,
    createdAt: user.createdAt,
  }));
  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageItems(size);
  };
  function handleFilter(value: string) {
    setFilter(value);
  }

  const handleSearch = (value: string) => {
    setSearchValue(value);
    // You can add your search logic here, e.g., fetching data based on the search term
  };
  useEffect(() => {
    if (filters) {
      setFilter(filters);
    }
  }, [filters]);
  useEffect(() => {
    if (error) {
      const e = parseClientError(error);
      if (e.statusCode === 403) {
        setUnauthorised(true);
      } else {
        setUnauthorised({
          title: "Error",
          message: e.message || "Something went wrong",
        });
      }
    }
  }, [error]);
  return (
    <div className={s.wrapper}>
      <div className={s.tableWrapper}>
        <div className={s.formWrapper}>
          <div className={s.params}>
            <div className={s.searchInput}>
              <SearchInput
                style={{ width: "100%", height: "100%" }}
                placeholder="search"
                width={332}
                height={45}
                handleSearch={handleSearch}
              />
            </div>
            <SelectField
              bordered
              label={"Status:"}
              defaultValue={""}
              wrapperClassname={s.SelectWrapper}
              className={clsx(s.selectInput)}
              value={filter}
              onChange={(value: any) => handleFilter(value)}
              style={{ width: "100%", height: "100%" }}
              options={[
                { value: "", label: "All" }, // Option to reset the filter
                { value: "verified", label: "Verified" },
                { value: "unverified", label: "Unverified" },
              ]}
            />
          </div>
          <div className={s.params}>
            <Paginate
              onChange={handlePageChange}
              total={TableUsers?.pagination.totalItems || 0}
              pageSize={pageItems}
            />
            <Button
              loading={loadings}
              onClick={downloadCsv}
              className={s.button}
            >
              Download CSV
            </Button>
          </div>
        </div>
        <ReusableTable
          loading={isLoading}
          noDataComponent={
            unauthorised ? (
              typeof unauthorised === "boolean" ? (
                <Unauthorised />
              ) : (
                <Unauthorised error={unauthorised} />
              )
            ) : (
              <NoData
                Message="No user yet"
                subMessage=""
                icon={<ContactsGroup fill="#F0EFEE" height={60} width={80} />}
              />
            )
          }
          dataSource={transformedData || []}
          columns={columns}
          //rowSelection={rowSelection} // Include row selection prop
        />
      </div>
    </div>
  );
}
export default Table;
